import React, { useEffect, useState, useCallback } from "react";
import {
  CalendarClock, CreditCard, Sparkles, AlertCircle, CheckCircle,
  MapPin, Globe, User, Phone, Check, ArrowRight, ShieldCheck,
  QrCode, Landmark, ChevronDown, ChevronUp, RefreshCw, Receipt
} from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "../context/AuthContext";
import {
  fetchPlans, purchasePlan, getMe,
  razorpayCreateOrder, razorpayVerifyPayment,
  fetchMyTransactions
} from "../api/client";
import "./Plans.css";

const EMPTY_KYC = {
  fullname: "",
  phone: "",
  country: "",
  address: "",
  pan_tax_id: ""
};

const EMPTY_CARD = {
  number: "",
  expiry: "",
  cvv: "",
  name: ""
};

// Dynamically load Razorpay checkout.js SDK
function loadRazorpaySDK() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function RenewPlan() {
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // Checkout wizard
  const [activePlan, setActivePlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1:KYC, 2:Payment method, 3:Fill, 4:Success
  const [kycForm, setKycForm] = useState(EMPTY_KYC);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [cardForm, setCardForm] = useState(EMPTY_CARD);
  const [upiTxnId, setUpiTxnId] = useState("");

  // Transaction history
  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [showTxnHistory, setShowTxnHistory] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPlans();
      setPlans(data);
    } catch {
      showToast("Failed to load available plans", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    setTxnLoading(true);
    try {
      const data = await fetchMyTransactions();
      setTransactions(data);
    } catch {
      setTransactions([]);
    } finally {
      setTxnLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
    loadTransactions();
  }, [loadPlans, loadTransactions]);

  const renderDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy, HH:mm");
  };

  const openCheckout = (plan) => {
    setActivePlan(plan);
    setCheckoutStep(1);
    setKycForm({
      fullname: user?.kyc_details?.fullname || user?.name || "",
      phone:    user?.kyc_details?.phone || "",
      country:  user?.kyc_details?.country || "",
      address:  user?.kyc_details?.address || "",
      pan_tax_id: user?.kyc_details?.pan_tax_id || ""
    });
    setCardForm(EMPTY_CARD);
    setUpiTxnId("");
    setPaymentMethod("razorpay");
    setShowCheckout(true);
  };

  const closeCheckout = () => { setShowCheckout(false); setActivePlan(null); };

  const handleKYCNext = () => {
    if (!kycForm.fullname.trim() || !kycForm.phone.trim() || !kycForm.country.trim() || !kycForm.address.trim()) {
      showToast("Please fill all KYC verification details", "error");
      return;
    }
    setCheckoutStep(2);
  };

  // ── RAZORPAY PAYMENT ──────────────────────────────────────────────────────
  const handleRazorpayPayment = async () => {
    setLoading(true);
    try {
      const sdkLoaded = await loadRazorpaySDK();
      if (!sdkLoaded) {
        showToast("Could not load Razorpay SDK. Check your internet connection.", "error");
        return;
      }

      let orderData;
      let isMock = false;
      try {
        orderData = await razorpayCreateOrder({
          plan_id: activePlan.id,
          billing_period: billingPeriod
        });
      } catch (err) {
        console.warn("Razorpay live order failed:", err);
        // Fallback to simulated payment order
        isMock = true;
        orderData = {
          order_id: "order_mock_" + Math.random().toString(36).substring(2, 9),
          amount: activePlan.price * 100,
          currency: "INR"
        };
      }

      if (isMock) {
        showToast("🔔 Razorpay Sandbox Mode activated (no keys found)", "info");
        setTimeout(async () => {
          const confirmPayment = window.confirm(
            `[RAZORPAY TEST SIMULATOR]\n\nDo you want to simulate a successful payment of ₹${getPrice(activePlan)}?`
          );
          if (confirmPayment) {
            try {
              const res = await razorpayVerifyPayment({
                razorpay_order_id:   orderData.order_id,
                razorpay_payment_id: "pay_mock_" + Math.random().toString(36).substring(2, 9),
                razorpay_signature:  "SIMULATED_SIGNATURE",
                plan_id:             activePlan.id,
                kyc:                 kycForm,
                billing_period:      billingPeriod
              });
              const updatedUser = await getMe();
              setUser(updatedUser);
              await loadTransactions();
              setCheckoutStep(4);
              showToast(res.message || "Simulated payment verified!");
            } catch (err) {
              showToast("Verification failed: " + (err.response?.data?.detail || err.message), "error");
            }
          }
          setLoading(false);
        }, 800);
        return;
      }

      const options = {
        key:         orderData.key_id,
        amount:      orderData.amount,
        currency:    orderData.currency,
        order_id:    orderData.order_id,
        name:        "PineConnector — TradeStreet",
        description: orderData.description,
        image:       "https://pineconnector.tradestreet.in/logo192.png",
        prefill: {
          name:    kycForm.fullname,
          contact: kycForm.phone,
        },
        theme: { color: "#00f2fe" },
        modal: { ondismiss: () => setLoading(false) },
        handler: async (response) => {
          try {
            // Verify payment on backend — activates plan immediately
            const res = await razorpayVerifyPayment({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              plan_id:             activePlan.id,
              kyc:                 kycForm,
              billing_period:      billingPeriod
            });
            // Refresh user session data instantly
            const updatedUser = await getMe();
            setUser(updatedUser);
            await loadTransactions();
            setCheckoutStep(4);
            showToast(res.message || "Payment successful! Plan activated.");
          } catch (err) {
            showToast(err.response?.data?.detail || "Payment verification failed. Contact support.", "error");
          } finally {
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        showToast(`Payment failed: ${response.error.description}`, "error");
        setLoading(false);
      });
      rzp.open();
    } catch (err) {
      showToast("Failed to initiate Razorpay checkout", "error");
      setLoading(false);
    }
  };

  // ── MANUAL / MOCK PAYMENT (card / UPI) ───────────────────────────────────
  const handleManualPurchase = async () => {
    if (paymentMethod === "card") {
      if (!cardForm.number || !cardForm.expiry || !cardForm.cvv || !cardForm.name) {
        showToast("Please enter complete card details", "error");
        return;
      }
      
      const rawCard = cardForm.number.replace(/\s/g, "");
      if (rawCard.length !== 16) {
        showToast("Card number must be exactly 16 digits", "error");
        return;
      }

      const expiryParts = cardForm.expiry.split("/");
      if (expiryParts.length !== 2 || expiryParts[0].length !== 2 || expiryParts[1].length !== 2) {
        showToast("Expiry must be in MM/YY format", "error");
        return;
      }

      const month = parseInt(expiryParts[0], 10);
      if (month < 1 || month > 12) {
        showToast("Expiry month must be between 01 and 12", "error");
        return;
      }

      if (cardForm.cvv.length !== 3) {
        showToast("CVV must be exactly 3 digits", "error");
        return;
      }

      if (cardForm.name.trim().length < 3) {
        showToast("Please enter a valid Cardholder Name", "error");
        return;
      }
    } else if (paymentMethod === "upi") {
      if (!upiTxnId.trim()) {
        showToast("Please enter the UPI Transaction ID for verification", "error");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await purchasePlan({
        plan_id: activePlan.id,
        kyc: kycForm,
        payment_method: paymentMethod,
        billing_period: billingPeriod
      });
      const updatedUser = await getMe();
      setUser(updatedUser);
      await loadTransactions();
      setCheckoutStep(4);
      showToast(res.message || "Purchase completed!");
    } catch (err) {
      showToast(err.response?.data?.detail || "Payment failed. Try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => {
    if (billingPeriod === "yearly") {
      return plan.yearly_price ?? Math.round(plan.price * 12 * 0.8);
    }
    return plan.price;
  };

  const displayedPlans = plans.length > 0 ? plans : [
    { id: "demo-1", name: "Starter", price: 19, yearly_price: 182, allowed_licenses: 1, mt5_per_license_allowed: 1, num_strategies_allowed: 1, support: "chatbot" },
    { id: "demo-2", name: "Professional", price: 39, yearly_price: 374, allowed_licenses: 3, mt5_per_license_allowed: 5, num_strategies_allowed: 5, support: "email, telegram" },
    { id: "demo-3", name: "Enterprise", price: 99, yearly_price: 950, allowed_licenses: 10, mt5_per_license_allowed: 20, num_strategies_allowed: 99, support: "chatbot, email, telegram" },
  ];

  // Status dot colour for transactions
  const statusColor = (status) => status === "success" ? "var(--green)" : status === "pending" ? "var(--accent)" : "var(--red)";

  return (
    <div className="plans-container">
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">Pricing &amp; Subscriptions</div>
          <div className="page-sub">Review plan metrics, complete KYC, choose a premium copier</div>
        </div>
      </div>

      {/* ── CURRENT PLAN STATUS ─────────────────────────────────────────── */}
      <div className="card" style={{ padding: "24px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <CalendarClock size={20} className="accent" />
            <h3 style={{ fontSize: "16px", fontWeight: "800", color: "var(--accent)" }}>Current Validity Status</h3>
          </div>
          <span className="badge badge-ok" style={{ textTransform: "uppercase", fontSize: "11px" }}>Active</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
          {[
            { label: "Active Package",      value: user?.service_plan?.toUpperCase() || "DEMO" },
            { label: "Licenses Authorized", value: `${user?.allowed_licenses || 1} IDs` },
            { label: "Service Start Date",  value: renderDate(user?.service_start) },
            { label: "Expiration Date",     value: renderDate(user?.service_end) }
          ].map(({ label, value }) => (
            <div key={label} style={{ background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "12px", border: "1px solid var(--border)" }}>
              <div style={{ color: "var(--text2)", fontSize: "12px", fontWeight: "600", marginBottom: "6px" }}>{label}</div>
              <div style={{ fontSize: "18px", fontWeight: "900", color: "var(--text)" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── BILLING PERIOD TOGGLE ──────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "center", margin: "0 0 28px" }}>
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "30px", padding: "4px", display: "inline-flex", gap: "4px", boxShadow: "0 4px 15px rgba(0,0,0,0.15)" }}>
          <button
            className={`btn ${billingPeriod === "monthly" ? "btn-primary" : "btn-ghost"}`}
            style={{ borderRadius: "25px", padding: "8px 24px", fontSize: "13px", height: "36px", cursor: "pointer" }}
            onClick={() => setBillingPeriod("monthly")}
          >Monthly billing</button>
          <button
            className={`btn ${billingPeriod === "yearly" ? "btn-primary" : "btn-ghost"}`}
            style={{ borderRadius: "25px", padding: "8px 24px", fontSize: "13px", height: "36px", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
            onClick={() => setBillingPeriod("yearly")}
          >
            Yearly billing
            <span style={{ fontSize: "10px", background: "var(--green)", color: "#111827", padding: "1px 6px", borderRadius: "10px", fontWeight: "700" }}>Save 20%</span>
          </button>
        </div>
      </div>

      {/* ── PLANS GRID ─────────────────────────────────────────────────── */}
      <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--text)", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "1px" }}>
        Available Plans
      </h3>
      <div className="plans-grid">
        {displayedPlans.map(plan => {
          const isSameTier = user?.service_plan?.toLowerCase() === plan.name.toLowerCase();
          const isMonthlyActive = user?.billing_period === "monthly";
          const isYearlyActive = user?.billing_period === "yearly";

          // Show switch option if they have a monthly tier of this plan and view yearly pricing
          const showSwitchToAnnually = isSameTier && isMonthlyActive && billingPeriod === "yearly";

          // Standard "Current Active Card" indicator applies if matched billing and tier
          const isCurrent = isSameTier && (
            (billingPeriod === "monthly" && isMonthlyActive) ||
            (billingPeriod === "yearly" && isYearlyActive)
          );

          const price = getPrice(plan);
          return (
            <div key={plan.id} className={`plan-card ${isCurrent ? "current-active" : ""}`}>
              {isCurrent && <span className="current-badge">Active Plan</span>}
              {showSwitchToAnnually && <span className="current-badge" style={{ background: "var(--accent)" }}>Monthly Active</span>}
              <div className="plan-header">
                <div className="plan-tier-name">{plan.name}</div>
                <div className="plan-price-box">
                  <span className="plan-currency-symbol">₹</span>
                  <span className="plan-price-value">{price}</span>
                  <span className="plan-price-period">{billingPeriod === "yearly" ? "/ year" : "/ month"}</span>
                </div>
              </div>
              <ul className="plan-feature-list">
                <li className="plan-feature-row"><Check size={14} className="plan-feature-icon" /><span><strong>{plan.allowed_licenses}</strong> License {plan.allowed_licenses === 1 ? "ID" : "IDs"}</span></li>
                <li className="plan-feature-row"><Check size={14} className="plan-feature-icon" /><span><strong>{plan.mt5_per_license_allowed}</strong> MT5 accounts / license</span></li>
                <li className="plan-feature-row"><Check size={14} className="plan-feature-icon" /><span><strong>{plan.num_strategies_allowed}</strong> TV strategies built</span></li>
                <li className="plan-feature-row"><Check size={14} className="plan-feature-icon" /><span style={{ textTransform: "capitalize" }}><strong>{plan.support}</strong> support</span></li>
              </ul>
              {plan.name.toLowerCase() === "demo" ? (
                <div style={{
                  width: "100%", textAlign: "center", color: "var(--text2)",
                  padding: "12px", borderRadius: "10px", marginTop: "auto",
                  border: "1px dashed var(--border)", background: "rgba(255,255,255,0.01)",
                  fontSize: "13px", fontWeight: "700"
                }}>
                  Default Plan (No Purchase Option)
                </div>
              ) : (
                <button
                  className={`btn ${isCurrent ? "btn-ghost" : "btn-primary"}`}
                  style={{ width: "100%", justifyContent: "center", padding: "12px", borderRadius: "10px", marginTop: "auto" }}
                  disabled={(isCurrent && !showSwitchToAnnually) || loading}
                  onClick={() => openCheckout(plan)}
                >
                  {isCurrent ? "Current Plan" : (showSwitchToAnnually ? "Switch to Annually" : `Purchase ${plan.name}`)}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── TRANSACTION HISTORY ─────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: "32px", padding: "0", overflow: "hidden" }}>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", cursor: "pointer", background: "rgba(255,255,255,0.02)", borderBottom: showTxnHistory ? "1px solid var(--border)" : "none" }}
          onClick={() => { setShowTxnHistory(p => !p); if (!showTxnHistory) loadTransactions(); }}
        >
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Receipt size={18} className="accent" />
            <h3 style={{ fontSize: "15px", fontWeight: "800", color: "var(--text)" }}>My Payment History</h3>
            {transactions.length > 0 && (
              <span className="badge badge-ok" style={{ fontSize: "11px" }}>{transactions.length} transactions</span>
            )}
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {showTxnHistory && (
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: "11px", height: "28px" }} onClick={e => { e.stopPropagation(); loadTransactions(); }}>
                <RefreshCw size={12} /> Refresh
              </button>
            )}
            {showTxnHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>

        {showTxnHistory && (
          <div style={{ padding: "20px 24px" }}>
            {txnLoading ? (
              <div style={{ textAlign: "center", padding: "30px", color: "var(--text2)" }}>Loading transactions…</div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px", color: "var(--text2)", fontSize: "14px" }}>
                No payment records found. Purchase a plan to see your history here.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date &amp; Time</th>
                      <th>Plan</th>
                      <th>Billing</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Payment ID</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(txn => (
                      <tr key={txn.id}>
                        <td style={{ fontSize: "12px", color: "var(--text2)" }}>{renderDate(txn.purchased_at)}</td>
                        <td style={{ fontWeight: "700", color: "var(--accent)" }}>{txn.plan_name}</td>
                        <td style={{ textTransform: "capitalize", fontSize: "12px" }}>{txn.billing_period || "monthly"}</td>
                        <td style={{ fontWeight: "800", color: "var(--text)" }}>₹{Number(txn.amount).toFixed(2)}</td>
                        <td style={{ textTransform: "uppercase", fontSize: "11px", fontWeight: "700", color: "var(--text2)" }}>{txn.payment_method}</td>
                        <td style={{ fontSize: "11px", fontFamily: "monospace", color: "var(--text2)" }}>
                          {txn.razorpay_payment_id || txn.razorpay_order_id || "—"}
                        </td>
                        <td>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "700", color: statusColor(txn.status) }}>
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: statusColor(txn.status), display: "inline-block" }} />
                            {(txn.status || "success").toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── CHECKOUT MODAL ──────────────────────────────────────────────── */}
      {showCheckout && (
        <div className="checkout-modal-overlay">
          <div className="checkout-modal">

            {/* Header */}
            <div className="checkout-header">
              <h4 className="checkout-title">
                Checkout: {activePlan?.name} — ₹{getPrice(activePlan)} / {billingPeriod === "yearly" ? "year" : "month"}
              </h4>
              <button className="close-modal-btn" onClick={closeCheckout}>✕</button>
            </div>

            {/* Step indicators */}
            <div className="wizard-steps">
              {["KYC Verify", "Payment", "Pay Details"].map((label, i) => (
                <div key={label} className={`wizard-step ${checkoutStep === i + 1 ? "active" : checkoutStep > i + 1 ? "completed" : ""}`}>
                  <span className="step-number">{i + 1}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>

            {/* Step content */}
            <div className="checkout-content">

              {/* STEP 1 — KYC */}
              {checkoutStep === 1 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", color: "var(--accent)", marginBottom: "8px" }}>
                    <ShieldCheck size={18} />
                    <span style={{ fontSize: "13px", fontWeight: "700" }}>Regulatory KYC verification required</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>Full Name (as on ID)</label>
                    <input className="form-input" placeholder="John Doe" value={kycForm.fullname}
                      onChange={e => setKycForm(p => ({ ...p, fullname: e.target.value }))} />
                  </div>
                  <div className="grid-2">
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "11px" }}>Mobile Phone</label>
                      <input className="form-input" placeholder="+91 9876543210" value={kycForm.phone}
                        onChange={e => setKycForm(p => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: "11px" }}>Country</label>
                      <input className="form-input" placeholder="India" value={kycForm.country}
                        onChange={e => setKycForm(p => ({ ...p, country: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: "11px" }}>Residential Address</label>
                    <input className="form-input" placeholder="123 Main Street" value={kycForm.address}
                      onChange={e => setKycForm(p => ({ ...p, address: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* STEP 2 — Choose Payment Method */}
              {checkoutStep === 2 && (
                <div>
                  <p style={{ color: "var(--text2)", fontSize: "14px", marginBottom: "20px" }}>
                    Select your preferred payment gateway:
                  </p>
                  <div className="payment-selector-grid">
                    <button
                      className={`payment-option-btn ${paymentMethod === "razorpay" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("razorpay")}
                    >
                      <Landmark size={24} />
                      <span style={{ fontSize: "12px", fontWeight: "700" }}>Razorpay</span>
                      <span style={{ fontSize: "10px", color: "var(--green)", fontWeight: "600" }}>✓ Recommended</span>
                    </button>
                    <button
                      className={`payment-option-btn ${paymentMethod === "card" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("card")}
                    >
                      <CreditCard size={24} />
                      <span style={{ fontSize: "12px", fontWeight: "700" }}>Credit / Debit Card</span>
                    </button>
                    <button
                      className={`payment-option-btn ${paymentMethod === "upi" ? "active" : ""}`}
                      onClick={() => setPaymentMethod("upi")}
                    >
                      <QrCode size={24} />
                      <span style={{ fontSize: "12px", fontWeight: "700" }}>UPI / QR Scan</span>
                    </button>
                  </div>

                  {paymentMethod === "razorpay" && (
                    <div style={{ marginTop: "16px", padding: "12px", background: "rgba(0,242,254,0.06)", border: "1px solid rgba(0,242,254,0.2)", borderRadius: "10px", fontSize: "13px", color: "var(--text2)" }}>
                      🔒 Razorpay secure checkout — supports Cards, NetBanking, UPI, Wallets. Your payment is 100% encrypted.
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3 — Payment Details */}
              {checkoutStep === 3 && (
                <div>
                  {/* RAZORPAY */}
                  {paymentMethod === "razorpay" && (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <div style={{ background: "rgba(0,242,254,0.05)", border: "1px solid rgba(0,242,254,0.2)", borderRadius: "14px", padding: "28px", maxWidth: "360px", margin: "0 auto" }}>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--accent)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                          Razorpay Secure Checkout
                        </div>
                        <div style={{ fontSize: "26px", fontWeight: "900", color: "var(--text)", margin: "8px 0" }}>
                          ₹{getPrice(activePlan)}
                        </div>
                        <div style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "20px" }}>
                          {activePlan?.name} — {billingPeriod === "yearly" ? "Yearly" : "Monthly"} billing
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--text2)", lineHeight: "1.6" }}>
                          Click <strong>"Confirm Purchase &amp; Pay"</strong> below to open the Razorpay secure popup. Your plan will be activated automatically once payment is confirmed.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CARD */}
                  {paymentMethod === "card" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: "11px" }}>Card Number</label>
                        <input className="form-input" placeholder="4111 2222 3333 4444" maxLength={19}
                          value={cardForm.number}
                          onChange={e => {
                            let val = e.target.value.replace(/\D/g, "");
                            if (val.length > 16) val = val.substring(0, 16);
                            const formatted = val.match(/.{1,4}/g)?.join(" ") || val;
                            setCardForm(p => ({ ...p, number: formatted }));
                          }} />
                      </div>
                      <div className="grid-3">
                        <div className="form-group" style={{ gridColumn: "span 2" }}>
                          <label className="form-label" style={{ fontSize: "11px" }}>Cardholder Name</label>
                          <input className="form-input" placeholder="JOHN DOE" value={cardForm.name}
                            onChange={e => setCardForm(p => ({ ...p, name: e.target.value.toUpperCase() }))} />
                        </div>
                        <div className="form-group">
                          <label className="form-label" style={{ fontSize: "11px" }}>Expiry MM/YY</label>
                          <input className="form-input" placeholder="12/28" maxLength={5} value={cardForm.expiry}
                            onChange={e => {
                              let val = e.target.value.replace(/\D/g, "");
                              if (val.length > 4) val = val.substring(0, 4);
                              if (val.length > 2) {
                                val = val.substring(0, 2) + "/" + val.substring(2);
                              }
                              setCardForm(p => ({ ...p, expiry: val }));
                            }} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label" style={{ fontSize: "11px" }}>CVV</label>
                        <input className="form-input" type="password" placeholder="•••" maxLength={3}
                          value={cardForm.cvv}
                          onChange={e => setCardForm(p => ({ ...p, cvv: e.target.value.replace(/\D/g, "") }))}
                          style={{ width: "80px" }} />
                      </div>
                    </div>
                  )}

                  {/* UPI */}
                  {paymentMethod === "upi" && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", textAlign: "center" }}>
                      <p style={{ fontSize: "13px", color: "var(--text2)" }}>
                        Scan GPay / PhonePe QR to pay <strong>₹{getPrice(activePlan)}</strong> then enter the UTR ID:
                      </p>
                      <div style={{ background: "#fff", padding: "10px", borderRadius: "12px", display: "inline-block" }}>
                        <div style={{ width: "120px", height: "120px", background: "#f3f4f6", border: "4px solid #1f2937", display: "flex", alignItems: "center", justifyContent: "center", color: "#111827", fontWeight: "bold", fontSize: "12px" }}>
                          UPI QR
                        </div>
                      </div>
                      <div className="form-group" style={{ width: "100%", maxWidth: "300px" }}>
                        <label className="form-label" style={{ fontSize: "11px" }}>UPI Transaction UTR ID (12 digits)</label>
                        <input className="form-input" placeholder="123456789012" maxLength={12}
                          value={upiTxnId}
                          onChange={e => setUpiTxnId(e.target.value.replace(/\D/g, ""))}
                          style={{ textAlign: "center", letterSpacing: "2px" }} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4 — Success */}
              {checkoutStep === 4 && (
                <div className="celebration-view">
                  <div className="celebration-icon"><CheckCircle size={32} /></div>
                  <h4 style={{ fontSize: "20px", fontWeight: "900", color: "var(--green)", marginBottom: "8px" }}>
                    Payment Confirmed! 🎉
                  </h4>
                  <p style={{ color: "var(--text2)", fontSize: "13.5px", margin: "0 auto 24px", maxWidth: "380px" }}>
                    Your account has been upgraded to <strong>{activePlan?.name}</strong>. License counts and MT5 connection limits are now active!
                  </p>
                  <button className="btn btn-primary" style={{ margin: "0 auto" }} onClick={closeCheckout}>
                    Return to Dashboard
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {checkoutStep < 4 && (
              <div className="checkout-footer">
                <button className="btn btn-ghost" disabled={checkoutStep === 1 || loading}
                  onClick={() => setCheckoutStep(p => p - 1)}>Back</button>

                {checkoutStep === 1 && (
                  <button className="btn btn-primary" onClick={handleKYCNext} disabled={loading}>
                    Next Step <ArrowRight size={14} />
                  </button>
                )}
                {checkoutStep === 2 && (
                  <button className="btn btn-primary" onClick={() => setCheckoutStep(3)} disabled={loading}>
                    Proceed to Pay <ArrowRight size={14} />
                  </button>
                )}
                {checkoutStep === 3 && (
                  <button className="btn btn-primary"
                    onClick={paymentMethod === "razorpay" ? handleRazorpayPayment : handleManualPurchase}
                    disabled={loading}>
                    {loading ? "Processing…" : "Confirm Purchase & Pay"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
